import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  login as apiLogin, verifyTotp as apiVerifyTotp,
  logout as apiLogout, updateProfile as apiUpdateProfile, AuthUser, LoginResponse,
} from '../api/auth';

export interface AuthContextValue {
  user:          AuthUser | null;
  isLoading:     boolean;
  login:         (email: string, password: string) => Promise<{ totpRequired: boolean; challenge?: string }>;
  verifyTotp:    (challenge: string, code: string, isBackup?: boolean) => Promise<void>;
  logout:        () => Promise<void>;
  updateProfile: (data: { full_name?: string; email?: string }) => Promise<void>;
  isAdmin:       boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem('auth_user');
    return stored ? (JSON.parse(stored) as AuthUser) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const data = await apiLogin(email, password);
      if ('totp_required' in data && data.totp_required) {
        return { totpRequired: true, challenge: data.totp_challenge };
      }
      const ok = data as LoginResponse;
      localStorage.setItem('access_token', ok.access_token);
      localStorage.setItem('refresh_token', ok.refresh_token);
      localStorage.setItem('auth_user', JSON.stringify(ok.user));
      setUser(ok.user);
      return { totpRequired: false };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyTotp = useCallback(async (challenge: string, code: string, isBackup = false) => {
    setIsLoading(true);
    try {
      const data = await apiVerifyTotp(
        isBackup
          ? { totp_challenge: challenge, backup_code: code }
          : { totp_challenge: challenge, code }
      );
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      setUser(data.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (data: { full_name?: string; email?: string }) => {
    const updated = await apiUpdateProfile(data);
    localStorage.setItem('auth_user', JSON.stringify(updated));
    setUser(updated);
  }, []);

  const logout = useCallback(async () => {
    const rt = localStorage.getItem('refresh_token') ?? '';
    await apiLogout(rt);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_user');
    setUser(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, verifyTotp, logout, updateProfile, isAdmin: user?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
}
